<template>
    <div class="control-move-modal">
        <p class="cmm-hint">
            Move <strong>{{ movingLabel }}</strong> — click the position where it should land.
        </p>

        <div v-if="!destinations.length" class="cmm-empty-form">
            No other place to move this control to.
        </div>

        <div v-for="dest in destinations" :key="dest.targetParentId" class="cmm-dest">
            <div class="cmm-dest-head">
                <span class="cmm-dest-name">{{ dest.label }}</span>
                <span v-if="dest.targetParentId === sourceParentId" class="cmm-current">(current)</span>
            </div>

            <div class="cmm-slots">
                <!-- top of the container -->
                <button type="button" class="cmm-slot" @click="pick(dest.targetParentId, 'top')">
                    {{ dest.fields.length ? '↑ Top of section' : 'Move into this section' }}
                </button>

                <!-- each existing field followed by an "insert after it" slot -->
                <template v-for="cid in dest.fields">
                    <div class="cmm-field" :key="dest.targetParentId + '-f-' + cid">{{ labelOf(cid) }}</div>
                    <button
                        type="button"
                        class="cmm-slot"
                        :key="dest.targetParentId + '-a-' + cid"
                        @click="pick(dest.targetParentId, { afterControlId: cid })"
                    >↳ Move here</button>
                </template>
            </div>
        </div>

        <div class="cmm-actions">
            <button type="button" class="btn btn-sm btn-secondary" @click="cancel">Cancel</button>
        </div>
    </div>
</template>

<script>
export default {
    name: "ControlMoveModal",
    props: {
        // injected by GlobalModal: { control, sourceParentId }
        dataPackage: { type: Object, default: () => ({}) },
        // full form model, passed by GlobalModal
        formData: { type: Object, default: () => ({}) },
        permissions: { type: Object, default: () => ({}) }
    },
    computed: {
        movingId() {
            return this.dataPackage && this.dataPackage.control && this.dataPackage.control.uniqueId
        },
        sourceParentId() {
            return this.dataPackage && this.dataPackage.sourceParentId
        },
        movingLabel() {
            const c = this.dataPackage && this.dataPackage.control
            return (c && (c.label || c.name || c.uniqueId)) || 'this control'
        },
        /**
         * Flatten the form into a list of drop destinations in form order.
         * Normal/Toggleable section -> one destination (section.controls).
         * Tab section -> one destination per tab (row.controls), labelled "Section › Tab".
         * Each destination's `fields` excludes the control being moved (it can't drop after itself).
         */
        destinations() {
            const sections = (this.formData && this.formData.sections) || {}
            const rows = (this.formData && this.formData.rows) || {}
            const movingId = this.movingId

            const sorted = Object.values(sections)
                .slice()
                .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))

            const out = []
            const fieldsExclMoving = (arr) => (Array.isArray(arr) ? arr : []).filter(id => id !== movingId)

            sorted.forEach(section => {
                if (section.type === 'tab') {
                    (Array.isArray(section.rows) ? section.rows : []).forEach(rowId => {
                        const row = rows[rowId]
                        if (!row) return
                        const tabName = (row.extendData && row.extendData.tabName) || 'Tab'
                        out.push({
                            targetParentId: row.uniqueId,
                            label: `${section.headline || 'Section'} › ${tabName}`,
                            fields: fieldsExclMoving(row.controls)
                        })
                    })
                } else {
                    out.push({
                        targetParentId: section.uniqueId,
                        label: section.headline || '(Untitled section)',
                        fields: fieldsExclMoving(section.controls)
                    })
                }
            })

            return out
        }
    },
    methods: {
        labelOf(controlId) {
            const c = this.formData && this.formData.controls && this.formData.controls[controlId]
            return (c && (c.label || c.name)) || controlId
        },
        pick(targetParentId, anchor) {
            this.$emit('saveAndClose', { targetParentId, anchor })
        },
        cancel() {
            this.$emit('close', false)
        }
    }
}
</script>

<style lang="scss" scoped>
.control-move-modal {
    max-height: 70vh;
    overflow-y: auto;
    padding: 4px 2px;
}
.cmm-hint {
    font-size: 14px;
    color: #374151;
    margin-bottom: 12px;
}
.cmm-empty-form {
    color: #6b7280;
    font-style: italic;
    padding: 12px;
}
.cmm-dest {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    margin-bottom: 10px;
    overflow: hidden;
}
.cmm-dest-head {
    background: #f8fafc;
    padding: 8px 12px;
    font-weight: 700;
    color: #1f2937;
    border-bottom: 1px solid #e5e7eb;
}
.cmm-current {
    margin-left: 6px;
    font-weight: 500;
    font-size: 12px;
    color: #9b59b6;
}
.cmm-slots {
    padding: 6px 10px;
}
.cmm-field {
    padding: 6px 8px;
    color: #4b5563;
    font-size: 13px;
}
.cmm-slot {
    display: block;
    width: 100%;
    text-align: left;
    border: 1px dashed #c4b5fd;
    background: #faf5ff;
    color: #6d28d9;
    border-radius: 4px;
    padding: 5px 8px;
    margin: 3px 0;
    font-size: 12px;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s;
}
.cmm-slot:hover {
    background: #ede9fe;
    border-color: #8b5cf6;
}
.cmm-actions {
    margin-top: 10px;
    text-align: right;
}
</style>
